/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU128Decoder,
  getU128Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const CLAIM_RECEIPT_DISCRIMINATOR = new Uint8Array([
  223, 233, 11, 229, 124, 165, 207, 28,
]);

export function getClaimReceiptDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CLAIM_RECEIPT_DISCRIMINATOR,
  );
}

export type ClaimReceipt = {
  discriminator: ReadonlyUint8Array;
  /** The market this receipt belongs to */
  market: Address;
  /** The claimer's wallet */
  claimer: Address;
  /** Total winner tokens burned by this user */
  burnedAmount: bigint;
  /** Raw quote already paid to this receipt */
  rewardDebt: bigint;
  /** Bump seed for this PDA */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

export type ClaimReceiptArgs = {
  /** The market this receipt belongs to */
  market: Address;
  /** The claimer's wallet */
  claimer: Address;
  /** Total winner tokens burned by this user */
  burnedAmount: number | bigint;
  /** Raw quote already paid to this receipt */
  rewardDebt: number | bigint;
  /** Bump seed for this PDA */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link ClaimReceiptArgs} account data. */
export function getClaimReceiptEncoder(): FixedSizeEncoder<ClaimReceiptArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['market', getAddressEncoder()],
      ['claimer', getAddressEncoder()],
      ['burnedAmount', getU64Encoder()],
      ['rewardDebt', getU128Encoder()],
      ['bump', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 7)],
    ]),
    (value) => ({ ...value, discriminator: CLAIM_RECEIPT_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link ClaimReceipt} account data. */
export function getClaimReceiptDecoder(): FixedSizeDecoder<ClaimReceipt> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['market', getAddressDecoder()],
    ['claimer', getAddressDecoder()],
    ['burnedAmount', getU64Decoder()],
    ['rewardDebt', getU128Decoder()],
    ['bump', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 7)],
  ]);
}

/** Gets the codec for {@link ClaimReceipt} account data. */
export function getClaimReceiptCodec(): FixedSizeCodec<
  ClaimReceiptArgs,
  ClaimReceipt
> {
  return combineCodec(getClaimReceiptEncoder(), getClaimReceiptDecoder());
}

export function decodeClaimReceipt<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<ClaimReceipt, TAddress>;
export function decodeClaimReceipt<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<ClaimReceipt, TAddress>;
export function decodeClaimReceipt<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<ClaimReceipt, TAddress> | MaybeAccount<ClaimReceipt, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getClaimReceiptDecoder(),
  );
}

export async function fetchClaimReceipt<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<ClaimReceipt, TAddress>> {
  const maybeAccount = await fetchMaybeClaimReceipt(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeClaimReceipt<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<ClaimReceipt, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeClaimReceipt(maybeAccount);
}

export async function fetchAllClaimReceipt(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<ClaimReceipt>[]> {
  const maybeAccounts = await fetchAllMaybeClaimReceipt(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeClaimReceipt(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<ClaimReceipt>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeClaimReceipt(maybeAccount));
}

export function getClaimReceiptSize(): number {
  return 104;
}
